'use client';

import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Copy, Check, ArrowUpDown, ArrowDownNarrowWide, ArrowUpNarrowWide, List, ListOrdered } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { textSorterContent } from '../../data/text-sorter-content';

type SortType = 'line' | 'word';
type SortOrder = 'asc' | 'desc';
type SortCaseSensitive = boolean;

export default function TextSorter() {
    const [inputText, setInputText] = useState('');
    const [copied, setCopied] = useState(false);
    const [sortType, setSortType] = useState<SortType>('line');
    const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
    const [caseSensitive, setCaseSensitive] = useState<boolean>(false);
    const [removeDuplicates, setRemoveDuplicates] = useState<boolean>(false);
    const { toast } = useToast();

    const sortedText = useMemo(() => {
        if (!inputText.trim()) return '';

        let items: string[] = [];

        // Split input into items (lines or words)
        if (sortType === 'line') {
            items = inputText.split('\n').filter(line => line.trim() !== '');
        } else {
            // Split by any whitespace and filter out empty strings
            items = inputText.split(/\s+/).filter(word => word.trim() !== '');
        }

        // Remove duplicates if enabled
        if (removeDuplicates) {
            const uniqueItems = new Set(
                caseSensitive
                    ? items
                    : items.map(item => item.toLowerCase())
            );
            items = Array.from(uniqueItems);
        }

        // Sort the items
        const sorted = [...items].sort((a, b) => {
            const compareA = caseSensitive ? a : a.toLowerCase();
            const compareB = caseSensitive ? b : b.toLowerCase();

            return sortOrder === 'asc'
                ? compareA.localeCompare(compareB)
                : compareB.localeCompare(compareA);
        });

        // Join back with appropriate separator
        return sortType === 'line' ? sorted.join('\n') : sorted.join(' ');

    }, [inputText, sortType, sortOrder, caseSensitive, removeDuplicates]);

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const fileContent = event.target?.result as string;
            setInputText(fileContent);
        };
        reader.readAsText(file);
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(sortedText);
        setCopied(true);
        toast({
            title: 'Copied!',
            description: 'Sorted text has been copied to clipboard.',
        });
        setTimeout(() => setCopied(false), 2000);
    };

    const clearText = () => setInputText('');

    const toggleSortOrder = () => {
        setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    };

    const getItemCount = () => {
        if (!inputText.trim()) return 0;
        return sortType === 'line'
            ? inputText.split('\n').filter(line => line.trim() !== '').length
            : inputText.split(/\s+/).filter(word => word.trim() !== '').length;
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <ToolPageIntro title="Text Sorter">
                Sort text lines alphabetically with our free online tool. Organize lists, data, and text content in ascending or descending order instantly.
            </ToolPageIntro>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Input Text</CardTitle>
                            <CardDescription>Enter or paste your text below</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="relative">
                                <Textarea
                                    value={inputText}
                                    onChange={(e) => setInputText(e.target.value)}
                                    placeholder={
                                        sortType === 'line'
                                            ? "Enter text with multiple lines to sort..."
                                            : "Enter text with multiple words to sort..."
                                    }
                                    className="min-h-[200px] w-full pl-3"
                                    rows={8}
                                />
                                <div className="absolute right-3 top-3 flex space-x-2">
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={clearText}
                                        disabled={!inputText}
                                        className="h-9 w-9 bg-background/80 backdrop-blur-sm"
                                        aria-label="Clear text"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="h-4 w-4"
                                        >
                                            <path d="M3 6h18" />
                                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                        </svg>
                                    </Button>
                                </div>
                            </div>

                            <div className="flex items-center justify-between">
                                <div className="relative">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        asChild
                                        className="cursor-pointer"
                                    >
                                        <Label htmlFor="file-upload" className="cursor-pointer">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="16"
                                                height="16"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                className="mr-2 h-4 w-4"
                                            >
                                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                <polyline points="17 8 12 3 7 8" />
                                                <line x1="12" y1="3" x2="12" y2="15" />
                                            </svg>
                                            Upload File
                                            <input
                                                id="file-upload"
                                                type="file"
                                                onChange={handleFileUpload}
                                                className="hidden"
                                                accept=".txt,.md,.doc,.docx"
                                            />
                                        </Label>
                                    </Button>
                                </div>
                                <div className="text-sm text-muted-foreground">
                                    {getItemCount()} {sortType === 'line' ? 'lines' : 'words'} • {inputText.length} characters
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Sorting Options</CardTitle>
                            <CardDescription>Customize how text is sorted</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <div className="space-y-0.5">
                                        <Label>Sort by</Label>
                                        <p className="text-sm text-muted-foreground">
                                            Choose what to sort
                                        </p>
                                    </div>
                                    <Select value={sortType} onValueChange={(value) => setSortType(value as SortType)}>
                                        <SelectTrigger className="w-[180px]">
                                            <SelectValue placeholder="Select sort type" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="line">Lines</SelectItem>
                                            <SelectItem value="word">Words</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="flex items-center justify-between">
                                    <div className="space-y-0.5">
                                        <Label>Sort order</Label>
                                        <p className="text-sm text-muted-foreground">
                                            {sortOrder === 'asc' ? 'A → Z (Ascending)' : 'Z → A (Descending)'}
                                        </p>
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={toggleSortOrder}
                                        className="w-[180px] justify-between"
                                    >
                                        {sortOrder === 'asc' ? (
                                            <>
                                                <ArrowUpNarrowWide className="h-4 w-4 mr-2" />
                                                Ascending
                                            </>
                                        ) : (
                                            <>
                                                <ArrowDownNarrowWide className="h-4 w-4 mr-2" />
                                                Descending
                                            </>
                                        )}
                                    </Button>
                                </div>

                                <div className="flex items-center justify-between pt-2">
                                    <div className="space-y-0.5">
                                        <Label htmlFor="case-sensitive">Case sensitive</Label>
                                        <p className="text-sm text-muted-foreground">
                                            Differentiate between uppercase and lowercase
                                        </p>
                                    </div>
                                    <Switch
                                        id="case-sensitive"
                                        checked={caseSensitive}
                                        onCheckedChange={setCaseSensitive}
                                    />
                                </div>

                                <div className="flex items-center justify-between pt-2">
                                    <div className="space-y-0.5">
                                        <Label htmlFor="remove-duplicates">Remove duplicates</Label>
                                        <p className="text-sm text-muted-foreground">
                                            Show only unique {sortType === 'line' ? 'lines' : 'words'}
                                        </p>
                                    </div>
                                    <Switch
                                        id="remove-duplicates"
                                        checked={removeDuplicates}
                                        onCheckedChange={setRemoveDuplicates}
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="space-y-6">
                    <Card>
                        <CardHeader>
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div>
                                    <CardTitle>Sorted Text</CardTitle>
                                    <CardDescription>
                                        {sortOrder === 'asc' ? 'Ascending' : 'Descending'} order
                                        {removeDuplicates && ' • Duplicates removed'}
                                    </CardDescription>
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={copyToClipboard}
                                    disabled={!sortedText}
                                    className="h-9"
                                >
                                    {copied ? (
                                        <Check className="h-4 w-4 mr-2" />
                                    ) : (
                                        <Copy className="h-4 w-4 mr-2" />
                                    )}
                                    {copied ? 'Copied!' : 'Copy'}
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="relative">
                                <Textarea
                                    value={sortedText}
                                    readOnly
                                    className="min-h-[200px] w-full bg-muted/50 font-mono pl-3"
                                    rows={8}
                                    placeholder="Your sorted text will appear here..."
                                />
                            </div>
                            {sortedText && (
                                <div className="mt-2 text-sm text-muted-foreground">
                                    {sortedText.split(sortType === 'line' ? '\n' : ' ').filter(Boolean).length} {sortType === 'line' ? 'lines' : 'words'} • {sortedText.length} characters
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>About Text Sorter</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4 text-sm text-muted-foreground">
                            <p>
                                This tool helps you sort text in various ways. You can sort lines or words in ascending or descending order,
                                with options for case sensitivity and duplicate removal.
                            </p>

                            <div className="space-y-2">
                                <h3 className="font-medium text-foreground">Features:</h3>
                                <ul className="list-disc pl-5 space-y-1">
                                    <li>Sort lines or words alphabetically</li>
                                    <li>Ascending (A-Z) or descending (Z-A) order</li>
                                    <li>Case-sensitive and case-insensitive sorting</li>
                                    <li>Remove duplicate lines or words</li>
                                    <li>Copy results to clipboard with one click</li>
                                    <li>Upload text from files (.txt, .md, .doc, .docx)</li>
                                </ul>
                            </div>

                            <div className="space-y-2">
                                <h3 className="font-medium text-foreground">Tips:</h3>
                                <ul className="list-disc pl-5 space-y-1">
                                    <li>Use <strong>Case sensitive</strong> to differentiate between uppercase and lowercase letters</li>
                                    <li>Enable <strong>Remove duplicates</strong> to get only unique items</li>
                                    <li>For large texts, consider using line sorting for better performance</li>
                                    <li>You can also sort comma-separated values by selecting "Words" sort type</li>
                                </ul>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Informational Content */}
                <div className="mt-12">
                    <InformationalContent
                        title={textSorterContent.title}
                        description={textSorterContent.description}
                        content={textSorterContent.content}
                        faqs={textSorterContent.faqs}
                        relatedTools={textSorterContent.relatedTools}
                    />
                </div>
            </div>
        </div>
    );
}